import { createRouter, createWebHistory } from "vue-router";
import store from "@/store";
import Dashboard from "@/views/Dashboard.vue";
import Tables from "@/views/Tables.vue";
import Profile from "@/views/Profile.vue";
import SignIn from "@/views/SignIn.vue";
import TestManagement from "@/views/TestManagement.vue";
import TestPlan from "@/views/TestPlan.vue";
import TestPlanDetail from "@/views/TestPlanDetail.vue";
import TestRunDetail from "@/views/TestRunDetail.vue";
import AdminSetting from "@/views/AdminSetting.vue";
import MyTask from "@/views/MyTask.vue";
import TeamPlanManagement from "@/views/TeamPlanManagement.vue";
import QaTestCaseBoard from "@/views/QaTestCaseBoard.vue";
import RolePermission from "@/views/RolePermission.vue";
import Management from "@/views/Management.vue";
import AutomationTracking from "@/views/AutomationTracking.vue";
import SystemSetting from "@/views/SystemSetting.vue";
import SprintSetting from "@/views/components/SprintSetting.vue";
import MenuSetting from "@/views/components/MenuSetting.vue";
import AiPromptSetting from "@/views/components/AiPromptSetting.vue";
import ProjectOverviewHub from "@/views/ProjectOverviewHub.vue";
import ProjectOverviewBoard from "@/views/ProjectOverviewBoard.vue";
import PlanManagementHub from "@/views/PlanManagementHub.vue";
import QaManagementHub from "@/views/QaManagementHub.vue";
import QaTestRunBoard from "@/views/QaTestRunBoard.vue";
import QaTestReportBoard from "@/views/QaTestReportBoard.vue";
import { isSessionExpired, hasActiveSession } from "@/config/session";
import {
  canAccessRouteName,
  ensureMenuAccess,
  getDefaultRouteTarget,
  hasMenuAccessConfigured,
  routeRequiresMenuPermission,
} from "@/utils/menuAccess";
import { ensureFeatureAccess } from "@/utils/featureAccess";

const routes = [
  {
    path: "/",
    name: "Home",
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: Dashboard,
  },
  {
    path: "/dashboard-v2",
    redirect: { name: "Dashboard" },
  },
  {
    path: "/tables",
    name: "Tables",
    component: Tables,
  },
  {
    path: "/test-management",
    name: "Test Management",
    component: TestManagement,
  },
  {
    path: "/test-plan",
    name: "Test Plan",
    component: TestPlan,
  },
  {
    path: "/test-plan-team",
    name: "Plan Management Team",
    component: PlanManagementHub,
    redirect: { name: "Plan Team All" },
    children: [
      {
        path: "all",
        name: "Plan Team All",
        component: TeamPlanManagement,
        props: { teamScope: "all" },
      },
      {
        path: "team-a",
        name: "Plan Team A",
        component: TeamPlanManagement,
        props: { teamScope: "team a" },
      },
      {
        path: "team-b",
        name: "Plan Team B",
        component: TeamPlanManagement,
        props: { teamScope: "team b" },
      },
      {
        path: "team-c",
        name: "Plan Team C",
        component: TeamPlanManagement,
        props: { teamScope: "unassign" },
      },
    ],
  },
  {
    path: "/plan-management",
    name: "Plan Management",
    redirect: { name: "Plan Management Team" },
  },
  {
    path: "/plan-management/all",
    name: "Plan Management All",
    redirect: { name: "Plan Team All" },
  },
  {
    path: "/plan-management/team-a",
    name: "Plan Management Team A",
    redirect: { name: "Plan Team A" },
  },
  {
    path: "/plan-management/team-b",
    name: "Plan Management Team B",
    redirect: { name: "Plan Team B" },
  },
  {
    path: "/plan-management/team-c",
    name: "Plan Management Team C",
    redirect: { name: "Plan Team C" },
  },
  {
    path: "/qa-management",
    name: "QA Management",
    component: QaManagementHub,
    redirect: { name: "QA Test Case" },
    children: [
      {
        path: "test-case",
        name: "QA Test Case",
        component: QaTestCaseBoard,
      },
      {
        path: "test-run",
        name: "QA Test Run",
        component: QaTestRunBoard,
      },
      {
        path: "test-report",
        name: "QA Test Report",
        component: QaTestReportBoard,
      },
    ],
  },
  {
    path: "/test-plan/:id",
    name: "Test Plan Detail",
    component: TestPlanDetail,
  },
  {
    path: "/test-plan/:id/run/:runIndex",
    name: "Test Run Detail",
    component: TestRunDetail,
  },
  {
    path: "/my-tasks",
    name: "My Tasks",
    component: MyTask,
  },
  {
    path: "/profile",
    name: "Profile",
    component: Profile,
  },
  {
    path: "/admin-setting",
    name: "User Setting",
    component: AdminSetting,
  },
  {
    path: "/role-permission",
    name: "Role Permission",
    component: RolePermission,
  },
  {
    path: "/management",
    name: "Management",
    component: Management,
  },
  {
    path: "/automation",
    name: "Automation Tracking",
    component: AutomationTracking,
  },
  {
    path: "/system-setting",
    name: "System Setting",
    component: SystemSetting,
    redirect: { name: "Sprint Setting" },
    children: [
      {
        path: "sprint-setting",
        name: "Sprint Setting",
        component: SprintSetting,
      },
      {
        path: "menu-setting",
        name: "Menu Setting",
        component: MenuSetting,
      },
      {
        path: "ai-prompt-setting",
        name: "AI Prompt Setting",
        component: AiPromptSetting,
      },
    ],
  },
  {
    path: "/project-overview",
    name: "Project Overview",
    component: ProjectOverviewHub,
    redirect: { name: "Project Overview All" },
    children: [
      {
        path: "all",
        name: "Project Overview All",
        component: ProjectOverviewBoard,
        props: { scope: "all" },
      },
      {
        path: "my",
        name: "Project Overview My",
        component: ProjectOverviewBoard,
        props: { scope: "mine" },
      },
    ],
  },
  {
    path: "/sign-in",
    name: "Sign In",
    component: SignIn,
  },
  {
    path: "/sign-up",
    redirect: { name: "Sign In" },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  linkActiveClass: "active",
});

const PUBLIC_ROUTES = ["Sign In"];

router.beforeEach(async (to, _from, next) => {
  if (store.state.user && (isSessionExpired() || !hasActiveSession())) {
    store.commit("setUser", null);
  }

  const isAuthenticated = !!store.state.user && hasActiveSession();

  if (!PUBLIC_ROUTES.includes(to.name) && !isAuthenticated) {
    next({
      name: "Sign In",
      query: to.name === "Sign In" ? {} : { reason: "session_expired" },
    });
    return;
  }

  if (!isAuthenticated) {
    next();
    return;
  }

  let menus = [];
  try {
    menus = await ensureMenuAccess(store, router);
    await ensureFeatureAccess(store);
  } catch (e) {
    console.error("access check error:", e);
  }

  const defaultTarget = getDefaultRouteTarget(menus, router);
  const defaultName = router.resolve(defaultTarget).name;

  if (to.name === "Sign In") {
    next(defaultTarget);
    return;
  }

  if (to.name === "Home" || to.path === "/") {
    next(defaultTarget);
    return;
  }

  if (
    hasMenuAccessConfigured(menus) &&
    to.name &&
    routeRequiresMenuPermission(to.name) &&
    !canAccessRouteName(to.name, menus, router)
  ) {
    if (to.name !== defaultName) {
      next(defaultTarget);
    } else {
      next();
    }
    return;
  }

  next();
});

export default router;
